import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from 'auth/useAuth';
import {
  getMyDetails,
  updateMyProfile,
  leaveWorkspace,
} from 'api/users/usersApi';
import { UserProfile, UserWorkspace } from 'components/common/ObjectTypes';
import {
  inputClasses,
  buttonClasses,
  skeletonClasses,
  onTealText,
  listPageClasses,
} from 'components/common/tailwindClassnames';
import { HoverTooltip } from 'components/common/HoverTooltip';
import SelectInput from 'components/common/SelectInput';
import { AttentionIcon } from 'components/common/ProjectIcons';
import { portalLanguages } from 'components/common/ObjectTypes';
import {
  trackAccountProfileUpdated,
  trackAccountWorkspaceLeft,
} from 'analytics/analytics';
import LeaveWorkspaceModal from './LeaveWorkspaceModal';

const AccountPage = () => {
  const { t, i18n } = useTranslation();
  const { token, authenticated } = useAuth();

  const [pageLoaded, setPageLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [workspaces, setWorkspaces] = useState<UserWorkspace[]>([]);
  const [language, setLanguage] = useState(i18n.language);
  const [originalValues, setOriginalValues] = useState({
    firstName: '',
    lastName: '',
    contactEmail: '',
    language: i18n.language,
  });
  const languages = useMemo(() => portalLanguages(t), [t]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuperuser, setIsSuperuser] = useState(false);
  const [leaveModalWorkspace, setLeaveModalWorkspace] =
    useState<UserWorkspace | null>(null);

  const changesMade =
    firstName !== originalValues.firstName ||
    lastName !== originalValues.lastName ||
    contactEmail !== originalValues.contactEmail ||
    language !== originalValues.language;

  // True when the profile was loaded without a contact email — used to highlight
  // the field after the login-time redirect. Clears once the user saves a value.
  const contactEmailMissing = pageLoaded && originalValues.contactEmail === '';

  const populateForm = useCallback((profile: UserProfile) => {
    const fn = profile.first_name || '';
    const ln = profile.last_name || '';
    const ce = profile.contact_email || '';
    const lang = i18n.language;
    setFirstName(fn);
    setLastName(ln);
    setContactEmail(ce);
    setLanguage(lang);
    setWorkspaces(profile.workspaces || []);
    setIsSuperuser(!!profile.is_superuser);
    setOriginalValues({
      firstName: fn,
      lastName: ln,
      contactEmail: ce,
      language: lang,
    });
  }, []);

  useEffect(() => {
    const loadProfile = async () => {
      if (token) {
        try {
          const profile = await getMyDetails(token);
          if (profile) {
            populateForm(profile);
          }
        } catch {
          // Error handled by API interceptor
        }
        setPageLoaded(true);
      }
    };

    if (!pageLoaded) {
      loadProfile();
    }
  }, [authenticated, token, pageLoaded, populateForm]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!firstName.trim()) {
      newErrors.firstName = t('This field is required.');
    }
    if (!lastName.trim()) {
      newErrors.lastName = t('This field is required.');
    }
    if (!contactEmail.trim()) {
      newErrors.contactEmail = t('This field is required.');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())) {
      newErrors.contactEmail = t('Please enter a valid email address.');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    if (!token) return;

    setSaving(true);
    try {
      const updatedProfile = await updateMyProfile(token, {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        contact_email: contactEmail.trim(),
      });
      if (updatedProfile) {
        if (language !== originalValues.language) {
          await i18n.changeLanguage(language);
        }
        populateForm(updatedProfile);
        trackAccountProfileUpdated();
        toast.success(t('Profile updated'), {
          position: toast.POSITION.TOP_RIGHT,
        });
        window.dispatchEvent(
          new CustomEvent('userProfileUpdated', {
            detail: {
              first_name: updatedProfile.first_name,
              last_name: updatedProfile.last_name,
              contact_email: updatedProfile.contact_email,
            },
          }),
        );
      }
    } catch (err: any) {
      if (err?.response?.status !== 401) {
        toast.error(t('Something went wrong updating your profile.'), {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFirstName(originalValues.firstName);
    setLastName(originalValues.lastName);
    setContactEmail(originalValues.contactEmail);
    setLanguage(originalValues.language);
    setErrors({});
  };

  const handleLeaveWorkspace = async () => {
    if (!leaveModalWorkspace || !token) return;

    try {
      await leaveWorkspace(
        token,
        leaveModalWorkspace.workspace_id,
        leaveModalWorkspace.id,
      );
      setWorkspaces((prev) =>
        prev.filter((w) => w.workspace_id !== leaveModalWorkspace.workspace_id),
      );
      trackAccountWorkspaceLeft(leaveModalWorkspace.name);
    } catch (err: any) {
      if (err?.response?.status === 404) {
        toast.error(t('Workspace not found or you are no longer a member.'), {
          position: toast.POSITION.TOP_RIGHT,
        });
      } else if (err?.response?.status !== 401) {
        toast.error(t('Something went wrong leaving the workspace.'), {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } finally {
      setLeaveModalWorkspace(null);
    }
  };

  if (!pageLoaded) {
    const skeletonBar = (w: string, h: string = 'h-4') => (
      <div className={`${h} ${w} ${skeletonClasses.base}`} />
    );
    return (
      <div className="transition-all duration-300">
        <div className={listPageClasses.band}>
          <div className={listPageClasses.bandRow}>
            <h1 className={listPageClasses.title}>{t('Account')}</h1>
          </div>
        </div>
        <div className={`${listPageClasses.body} pb-56`}>
          <div className="mx-auto w-full max-w-list-table">
            <div>
              {skeletonBar('w-32', 'h-8 mb-2')}
              <div className="mb-6 space-y-1.5">
                {skeletonBar('w-full', 'h-3')}
                {skeletonBar('w-3/4', 'h-3')}
              </div>
              <div className="rounded-md border border-teal-t30 bg-white p-4">
                {/* First Name */}
                <div className="mb-6 flex flex-row items-center">
                  <div className="w-1/3">{skeletonBar('w-24')}</div>
                  <div className="w-2/5">{skeletonBar('w-full', 'h-9')}</div>
                </div>
                {/* Last Name */}
                <div className="mb-6 flex flex-row items-center">
                  <div className="w-1/3">{skeletonBar('w-24')}</div>
                  <div className="w-2/5">{skeletonBar('w-full', 'h-9')}</div>
                </div>
                {/* Workspaces */}
                <div className="mb-6 flex flex-row items-start">
                  <div className="mt-2 w-1/3">{skeletonBar('w-28')}</div>
                  <div className="w-2/3 space-y-2">
                    {skeletonBar('w-full', 'h-9')}
                    {skeletonBar('w-3/4', 'h-9')}
                  </div>
                </div>
                {/* Contact Email */}
                <div className="mb-6 flex flex-row items-center">
                  <div className="w-1/3">{skeletonBar('w-32')}</div>
                  <div className="w-2/5">{skeletonBar('w-full', 'h-9')}</div>
                </div>
                {/* Language */}
                <div className="flex flex-row items-center">
                  <div className="w-1/3">{skeletonBar('w-24')}</div>
                  <div className="w-2/5">{skeletonBar('w-full', 'h-9')}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="transition-all duration-300">
      <div className={listPageClasses.band}>
        <div className={listPageClasses.bandRow}>
          <h1 className={listPageClasses.title}>{t('Account')}</h1>
        </div>
      </div>
      <div className={`${listPageClasses.body} pb-56`}>
        <div className="mx-auto w-full max-w-list-table">
          <div>
            <p className={`${onTealText} mb-6`}>
              {t(
                'Keep your contact information up to date — it will be used for automatic alerts and notifications in the future. You can always return to this page by clicking the arrow next to your name and selecting Account.',
              )}
            </p>

            <div className="rounded-md border border-teal-t30 bg-white p-4">
              {/* First Name */}
              <div className="mb-6 flex flex-col justify-between md:flex-row md:items-start">
                <label className="mb-1 text-ui-text-primary-text md:mb-0 md:w-1/3 md:pt-2">
                  {t('First Name')}*
                </label>
                <div className="md:w-2/5">
                  <input
                    data-testid="AccountPage-firstName-input"
                    className={`w-full ${errors.firstName ? inputClasses.error : inputClasses.standard}`}
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setErrors((prev) => ({ ...prev, firstName: '' }));
                    }}
                  />
                  {errors.firstName && (
                    <div className="mt-1 flex flex-row items-center text-unlisted-orange">
                      <AttentionIcon />
                      <p className="ml-1 text-sm">{errors.firstName}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Last Name */}
              <div className="mb-6 flex flex-col justify-between md:flex-row md:items-start">
                <label className="mb-1 text-ui-text-primary-text md:mb-0 md:w-1/3 md:pt-2">
                  {t('Last Name')}*
                </label>
                <div className="md:w-2/5">
                  <input
                    data-testid="AccountPage-lastName-input"
                    className={`w-full ${errors.lastName ? inputClasses.error : inputClasses.standard}`}
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setErrors((prev) => ({ ...prev, lastName: '' }));
                    }}
                  />
                  {errors.lastName && (
                    <div className="mt-1 flex flex-row items-center text-unlisted-orange">
                      <AttentionIcon />
                      <p className="ml-1 text-sm">{errors.lastName}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Workspaces */}
              <div className="mb-6 flex flex-col justify-between md:flex-row md:items-start">
                <label className="mb-1 text-ui-text-primary-text md:mb-0 md:w-1/3 md:pt-2">
                  {t('Workspace(s)')}
                </label>
                <div className="md:w-2/3">
                  {workspaces.length > 0 ? (
                    <div className="overflow-visible rounded-md border border-unlisted-gray-lines">
                      {workspaces.map((ws) => (
                        <div
                          key={ws.workspace_id}
                          className="flex flex-row items-center border-b border-unlisted-gray-lines px-3 py-2 last:border-b-0"
                          data-testid={`AccountPage-workspace-${ws.workspace_id}`}
                        >
                          <Link
                            to={`/workspaces/${ws.workspace_id}`}
                            data-testid={`AccountPage-workspace-${ws.workspace_id}-link`}
                            className="flex-1 truncate text-ui-text-primary-text transition-colors duration-200 hover:text-ui-cta-primary hover:underline"
                          >
                            {ws.name}
                          </Link>
                          <span className="mr-5 min-w-[60px] text-right text-sm text-ui-text-primary-text">
                            {ws.role.charAt(0).toUpperCase() + ws.role.slice(1)}
                          </span>
                          {ws.role === 'admin' || isSuperuser ? (
                            <button
                              type="button"
                              data-testid={`AccountPage-leaveWorkspace-${ws.workspace_id}-button`}
                              className={buttonClasses.lightRed}
                              onClick={() => setLeaveModalWorkspace(ws)}
                            >
                              {t('Leave')}
                            </button>
                          ) : (
                            <HoverTooltip
                              position="top-right"
                              width="w-52"
                              content={t(
                                'Only admins can manage workspace membership.',
                              )}
                            >
                              <button
                                type="button"
                                data-testid={`AccountPage-leaveWorkspace-${ws.workspace_id}-button`}
                                disabled
                                className={buttonClasses.lightRed}
                              >
                                {t('Leave')}
                              </button>
                            </HoverTooltip>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-ui-text-primary-text">
                      {t('No workspaces')}
                    </p>
                  )}
                </div>
              </div>

              {/* Contact Email */}
              <div className="mb-6 flex flex-col justify-between md:flex-row md:items-start">
                <label className="mb-1 text-ui-text-primary-text md:mb-0 md:w-1/3 md:pt-2">
                  {t('Contact Email')}*
                </label>
                <div className="md:w-2/5">
                  {contactEmailMissing && (
                    <div
                      data-testid="AccountPage-contactEmail-missing-banner"
                      className="mb-2 flex flex-row items-center text-sm text-unlisted-orange"
                    >
                      <AttentionIcon />
                      <p className="ml-1">
                        {t('Please add a contact email to your account.')}
                      </p>
                    </div>
                  )}
                  <input
                    type="email"
                    data-testid="AccountPage-contactEmail-input"
                    className={`w-full ${errors.contactEmail || contactEmailMissing ? inputClasses.error : inputClasses.standard}`}
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      setErrors((prev) => ({ ...prev, contactEmail: '' }));
                    }}
                  />
                  {errors.contactEmail && (
                    <div className="mt-1 flex flex-row items-center text-unlisted-orange">
                      <AttentionIcon />
                      <p className="ml-1 text-sm">{errors.contactEmail}</p>
                    </div>
                  )}
                </div>
              </div>
              {/* Language */}
              <div className="flex flex-col justify-between md:flex-row md:items-start">
                <label
                  htmlFor="AccountPage-language-select"
                  className="mb-1 text-ui-text-primary-text md:mb-0 md:w-1/3 md:pt-2"
                >
                  {t('Language')}
                </label>
                <div
                  className="md:w-2/5"
                  data-testid="AccountPage-language-select"
                >
                  <SelectInput
                    inputId="AccountPage-language-select"
                    options={languages}
                    className="basic-single"
                    value={
                      languages.find((lang) => lang.value === language) ?? null
                    }
                    isDisabled={false}
                    isLoading={false}
                    isClearable={false}
                    isSearchable={false}
                    isMulti={false}
                    name="language"
                    onChange={(e: any) => setLanguage(e.value)}
                  />
                </div>
              </div>
            </div>

            {/* Footer actions. Opaque page tint, not a translucent white
                        over a blur: on the tinted page that washed out to a grey
                        band that read as a footer. And the buttons sit on the
                        same centred column as the title above, rather than
                        against the left edge of the whole bar. */}
            <div className="fixed bottom-0 left-0 z-10 w-full bg-teal-t10 py-3 md:pl-[110px]">
              <div className="px-4 md:px-8 desktop:px-[60px]">
                <div className="mx-auto flex w-full max-w-list-table flex-row gap-3">
                  <button
                    data-testid="AccountPage-save-button"
                    className={buttonClasses.primary}
                    disabled={!changesMade || saving}
                    onClick={handleSave}
                  >
                    {t('Save')}
                  </button>
                  <button
                    data-testid="AccountPage-cancel-button"
                    className={buttonClasses.cancel}
                    onClick={handleCancel}
                  >
                    {t('Cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {leaveModalWorkspace && (
        <LeaveWorkspaceModal
          workspaceName={leaveModalWorkspace.name}
          onConfirm={handleLeaveWorkspace}
          onCancel={() => setLeaveModalWorkspace(null)}
        />
      )}
    </div>
  );
};

export default AccountPage;
